import React, { useState } from 'react';
import { 
  BarChart3, TrendingUp, Users, CreditCard, DollarSign, 
  Calendar, Download, Filter, ArrowUpRight, ArrowDownRight,
  Eye, FileText, Building2, Percent, Clock, Target,
  ChevronDown, Search, Gift, Wallet, CreditCard as CreditCardIcon,
  BanknoteIcon, UserRound, Zap, CalendarRange, PieChart
} from 'lucide-react';

interface ReportData {
  id: string;
  name: string;
  type: 'bank' | 'product' | 'campaign';
  category?: string;
  views: number;
  clicks: number;
  conversions: number;
  conversionRate: number;
  revenue: number;
  dailyStats: {
    date: string;
    views: number;
    clicks: number;
    conversions: number;
  }[];
}

const Reports = () => {
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customStartDate, setCustomStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [customEndDate, setCustomEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [reportType, setReportType] = useState('overview');
  const [selectedBank, setSelectedBank] = useState('all');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [activeTab, setActiveTab] = useState<'banks' | 'products' | 'campaigns'>('banks');
  const [searchTerm, setSearchTerm] = useState('');

  // Sample data for the reports
  const stats = {
    totalApplications: {
      value: 1247,
      change: 12.5,
      trend: 'up'
    },
    conversionRate: {
      value: 8.9,
      change: -2.1,
      trend: 'down'
    },
    revenue: {
      value: 45600,
      change: 18.3,
      trend: 'up'
    },
    activeUsers: {
      value: 892,
      change: 5.7,
      trend: 'up'
    }
  };

  const banks = [
    { id: 'all', name: 'Tüm Bankalar' },
    { id: 'akbank', name: 'Akbank', logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png' },
    { id: 'garanti', name: 'Garanti BBVA', logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png' },
    { id: 'yapikredi', name: 'Yapı Kredi', logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png' },
    { id: 'qnb', name: 'QNB Finansbank', logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png' },
    { id: 'isbankasi', name: 'İş Bankası', logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png' }
  ];

  const products = [
    { id: 'all', name: 'Tüm Ürünler' },
    { id: 'ihtiyac-kredisi', name: 'İhtiyaç Kredisi', category: 'loan' },
    { id: 'konut-kredisi', name: 'Konut Kredisi', category: 'loan' },
    { id: 'tasit-kredisi', name: 'Taşıt Kredisi', category: 'loan' },
    { id: 'kobi-kredisi', name: 'Kobi Kredisi', category: 'loan' },
    { id: 'kredi-karti', name: 'Kredi Kartı', category: 'card' },
    { id: 'mevduat', name: 'Mevduat', category: 'deposit' },
    { id: 'musteri-edinimi', name: 'Müşteri Edinimi', category: 'remote_customer' }
  ];

  const campaigns = [
    { id: 'all', name: 'Tüm Kampanyalar' },
    { id: 'yeni-musteri', name: 'Yeni Müşteri Kampanyası', bank: 'akbank' },
    { id: 'hos-geldin', name: 'Hoş Geldin Kredisi', bank: 'akbank' },
    { id: 'uzaktan-musteri', name: 'Uzaktan Müşteri Edinimi Kampanyası', bank: 'garanti' }
  ];

  // Sample report data
  const reportData: ReportData[] = [
    {
      id: 'akbank',
      name: 'Akbank',
      type: 'bank',
      views: 12500,
      clicks: 3200,
      conversions: 450,
      conversionRate: 14.1,
      revenue: 22500,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 500) + 300,
        clicks: Math.floor(Math.random() * 150) + 50,
        conversions: Math.floor(Math.random() * 20) + 5
      }))
    },
    {
      id: 'garanti',
      name: 'Garanti BBVA',
      type: 'bank',
      views: 10800,
      clicks: 2800,
      conversions: 380,
      conversionRate: 13.6,
      revenue: 19000,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 450) + 250,
        clicks: Math.floor(Math.random() * 130) + 40,
        conversions: Math.floor(Math.random() * 18) + 4
      }))
    },
    {
      id: 'yapikredi',
      name: 'Yapı Kredi',
      type: 'bank',
      views: 9500,
      clicks: 2400,
      conversions: 320,
      conversionRate: 13.3,
      revenue: 16000,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 400) + 200,
        clicks: Math.floor(Math.random() * 120) + 30,
        conversions: Math.floor(Math.random() * 15) + 3
      }))
    },
    {
      id: 'ihtiyac-kredisi',
      name: 'İhtiyaç Kredisi',
      type: 'product',
      category: 'loan',
      views: 15000,
      clicks: 4200,
      conversions: 580,
      conversionRate: 13.8,
      revenue: 29000,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 600) + 400,
        clicks: Math.floor(Math.random() * 180) + 80,
        conversions: Math.floor(Math.random() * 25) + 10
      }))
    },
    {
      id: 'kredi-karti',
      name: 'Kredi Kartı',
      type: 'product',
      category: 'card',
      views: 12000,
      clicks: 3500,
      conversions: 420,
      conversionRate: 12.0,
      revenue: 21000,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 500) + 300,
        clicks: Math.floor(Math.random() * 150) + 60,
        conversions: Math.floor(Math.random() * 20) + 8
      }))
    },
    {
      id: 'yeni-musteri',
      name: 'Yeni Müşteri Kampanyası',
      type: 'campaign',
      views: 8500,
      clicks: 2200,
      conversions: 310,
      conversionRate: 14.1,
      revenue: 15500,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 350) + 200,
        clicks: Math.floor(Math.random() * 100) + 40,
        conversions: Math.floor(Math.random() * 15) + 5
      }))
    },
    {
      id: 'hos-geldin',
      name: 'Hoş Geldin Kredisi',
      type: 'campaign',
      views: 7200,
      clicks: 1800,
      conversions: 250,
      conversionRate: 13.9,
      revenue: 12500,
      dailyStats: Array.from({ length: 30 }, (_, i) => ({
        date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        views: Math.floor(Math.random() * 300) + 150,
        clicks: Math.floor(Math.random() * 80) + 30,
        conversions: Math.floor(Math.random() * 12) + 4
      }))
    }
  ];

  // Filter report data based on selections
  const getFilteredReportData = () => {
    let filtered = [...reportData];
    
    // Filter by type
    if (activeTab === 'banks') {
      filtered = filtered.filter(item => item.type === 'bank');
    } else if (activeTab === 'products') {
      filtered = filtered.filter(item => item.type === 'product');
    } else if (activeTab === 'campaigns') {
      filtered = filtered.filter(item => item.type === 'campaign');
    }
    
    // Filter by bank
    if (selectedBank !== 'all') {
      filtered = filtered.filter(item => item.id === selectedBank || item.id.includes(selectedBank));
    }
    
    // Filter by product
    if (selectedProduct !== 'all') {
      filtered = filtered.filter(item => item.id === selectedProduct || item.id.includes(selectedProduct));
    }
    
    // Filter by campaign
    if (selectedCampaign !== 'all') {
      filtered = filtered.filter(item => item.id === selectedCampaign);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const filteredData = getFilteredReportData();

  // Calculate totals for the filtered data
  const totals = filteredData.reduce((acc, item) => {
    return {
      views: acc.views + item.views,
      clicks: acc.clicks + item.clicks,
      conversions: acc.conversions + item.conversions,
      revenue: acc.revenue + item.revenue
    };
  }, { views: 0, clicks: 0, conversions: 0, revenue: 0 });

  // Calculate overall conversion rate
  const overallConversionRate = totals.clicks > 0 
    ? ((totals.conversions / totals.clicks) * 100).toFixed(1) 
    : '0.0';

  // Get daily stats for chart
  const getDailyStats = () => {
    // Create a map to store aggregated stats by date
    const statsByDate = new Map();
    
    // Initialize with dates for the last 30 days
    for (let i = 0; i < 30; i++) {
      const date = new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      statsByDate.set(date, { date, views: 0, clicks: 0, conversions: 0 });
    }
    
    // Aggregate stats from filtered data
    filteredData.forEach(item => {
      item.dailyStats.forEach(stat => {
        if (statsByDate.has(stat.date)) {
          const current = statsByDate.get(stat.date);
          statsByDate.set(stat.date, {
            date: stat.date,
            views: current.views + stat.views,
            clicks: current.clicks + stat.clicks,
            conversions: current.conversions + stat.conversions
          });
        }
      });
    });
    
    // Convert map to array and sort by date
    return Array.from(statsByDate.values()).sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );
  };

  const dailyStats = getDailyStats();

  // Get formatted date range for display
  const getFormattedDateRange = () => {
    if (showCustomDateRange) {
      return `${new Date(customStartDate).toLocaleDateString('tr-TR')} - ${new Date(customEndDate).toLocaleDateString('tr-TR')}`;
    }
    
    switch (dateRange) {
      case 'today':
        return 'Bugün';
      case 'yesterday':
        return 'Dün';
      case 'last_7_days':
        return 'Son 7 Gün';
      case 'last_30_days':
        return 'Son 30 Gün';
      case 'this_month':
        return 'Bu Ay';
      case 'last_month':
        return 'Geçen Ay';
      case 'this_year':
        return 'Bu Yıl';
      default:
        return 'Son 30 Gün';
    }
  };

  // Get icon for report item
  const getItemIcon = (item: ReportData) => {
    if (item.type === 'bank') {
      return <Building2 size={20} className="text-blue-600" />;
    } else if (item.type === 'product') {
      if (item.category === 'loan') {
        return <BanknoteIcon size={20} className="text-green-600" />;
      } else if (item.category === 'card') {
        return <CreditCardIcon size={20} className="text-purple-600" />;
      } else if (item.category === 'deposit') {
        return <Wallet size={20} className="text-teal-600" />;
      } else {
        return <UserRound size={20} className="text-indigo-600" />;
      }
    } else {
      return <Gift size={20} className="text-orange-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Raporlar</h1>
              <p className="text-slate-600">Detaylı analitik raporları ve istatistikler</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  onClick={() => setShowCustomDateRange(!showCustomDateRange)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Calendar size={20} className="text-slate-500" />
                  <span>{getFormattedDateRange()}</span>
                  <ChevronDown size={16} className={`text-slate-500 transition-transform ${showCustomDateRange ? 'rotate-180' : ''}`} />
                </button>
                
                {showCustomDateRange && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 p-4 z-10">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Hazır Tarih Aralıkları
                        </label>
                        <select
                          value={dateRange}
                          onChange={(e) => setDateRange(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        >
                          <option value="today">Bugün</option>
                          <option value="yesterday">Dün</option>
                          <option value="last_7_days">Son 7 Gün</option>
                          <option value="last_30_days">Son 30 Gün</option>
                          <option value="this_month">Bu Ay</option>
                          <option value="last_month">Geçen Ay</option>
                          <option value="this_year">Bu Yıl</option>
                          <option value="custom">Özel Tarih Aralığı</option>
                        </select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Başlangıç Tarihi
                          </label>
                          <input
                            type="date"
                            value={customStartDate}
                            onChange={(e) => setCustomStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            Bitiş Tarihi
                          </label>
                          <input
                            type="date"
                            value={customEndDate}
                            onChange={(e) => setCustomEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end">
                        <button
                          onClick={() => setShowCustomDateRange(false)}
                          className="px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                        >
                          Uygula
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <button className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors">
                <Download size={20} />
                <span>Rapor İndir</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Toplam Görüntülenme</p>
                <p className="text-2xl font-bold text-slate-900">{totals.views.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight size={16} className="text-green-600" />
                  <span className="text-sm text-green-600">+8.5%</span>
                  <span className="text-sm text-slate-500">önceki döneme göre</span>
                </div>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Eye className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Tıklamalar</p>
                <p className="text-2xl font-bold text-slate-900">{totals.clicks.toLocaleString()}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight size={16} className="text-green-600" />
                  <span className="text-sm text-green-600">+12.3%</span>
                  <span className="text-sm text-slate-500">önceki döneme göre</span>
                </div>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <Target className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Dönüşüm Oranı</p>
                <p className="text-2xl font-bold text-slate-900">%{overallConversionRate}</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowDownRight size={16} className="text-red-600" />
                  <span className="text-sm text-red-600">-2.1%</span>
                  <span className="text-sm text-slate-500">önceki döneme göre</span>
                </div>
              </div>
              <div className="bg-purple-100 p-3 rounded-lg">
                <Percent className="text-purple-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Gelir</p>
                <p className="text-2xl font-bold text-slate-900">{totals.revenue.toLocaleString()} ₺</p>
                <div className="flex items-center gap-1 mt-2">
                  <ArrowUpRight size={16} className="text-green-600" />
                  <span className="text-sm text-green-600">+15.8%</span>
                  <span className="text-sm text-slate-500">önceki döneme göre</span>
                </div>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <DollarSign className="text-orange-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Tabs */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* Tabs */}
            <div className="flex border border-slate-200 rounded-lg p-1 bg-slate-50">
              <button
                onClick={() => setActiveTab('banks')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'banks'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Building2 size={16} />
                  <span>Bankalar</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'products'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CreditCardIcon size={16} />
                  <span>Ürünler</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('campaigns')}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'campaigns'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Gift size={16} />
                  <span>Kampanyalar</span>
                </div>
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full md:w-64 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
          </div>

          {/* Additional Filters */}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {activeTab === 'banks' || activeTab === 'products' || activeTab === 'campaigns' ? (
              <>
                {/* Bank Filter */}
                {activeTab !== 'banks' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Banka
                    </label>
                    <select
                      value={selectedBank}
                      onChange={(e) => setSelectedBank(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    >
                      {banks.map(bank => (
                        <option key={bank.id} value={bank.id}>{bank.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Product Filter */}
                {activeTab !== 'products' && activeTab !== 'banks' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Ürün
                    </label>
                    <select
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    >
                      {products.map(product => (
                        <option key={product.id} value={product.id}>{product.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Campaign Filter */}
                {activeTab !== 'campaigns' && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">
                      Kampanya
                    </label>
                    <select
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    >
                      {campaigns.map(campaign => (
                        <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Date Range Filter */}
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">
                    Tarih Aralığı
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                    <span className="text-slate-400">-</span>
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                    />
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* Report Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-6">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between">
              <h2 className="font-medium text-slate-800 flex items-center gap-2">
                {activeTab === 'banks' && <Building2 size={18} className="text-[#F7BA45]" />}
                {activeTab === 'products' && <CreditCardIcon size={18} className="text-[#F7BA45]" />}
                {activeTab === 'campaigns' && <Gift size={18} className="text-[#F7BA45]" />}
                <span>
                  {activeTab === 'banks' && 'Banka Bazlı Raporlar'}
                  {activeTab === 'products' && 'Ürün Bazlı Raporlar'}
                  {activeTab === 'campaigns' && 'Kampanya Bazlı Raporlar'}
                </span>
                <span className="text-sm text-slate-500 ml-2">
                  ({getFormattedDateRange()})
                </span>
              </h2>
              <button className="flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={16} />
                <span>CSV İndir</span>
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {activeTab === 'banks' ? 'Banka' : activeTab === 'products' ? 'Ürün' : 'Kampanya'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Görüntülenme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tıklama
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Dönüşüm
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Dönüşüm Oranı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Gelir
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {getItemIcon(item)}
                        <div className="text-sm font-medium text-slate-900">{item.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{item.views.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{item.clicks.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{item.conversions.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">%{item.conversionRate.toFixed(1)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-900">{item.revenue.toLocaleString()} ₺</div>
                    </td>
                  </tr>
                ))}
                
                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <PieChart size={40} className="text-slate-300 mb-2" />
                        <p>Seçilen kriterlere uygun veri bulunamadı</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              
              {filteredData.length > 0 && (
                <tfoot className="bg-slate-50 border-t border-slate-200">
                  <tr>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">Toplam</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{totals.views.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{totals.clicks.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{totals.conversions.toLocaleString()}</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">%{overallConversionRate}</div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap">
                      <div className="text-sm font-semibold text-slate-900">{totals.revenue.toLocaleString()} ₺</div>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Daily Stats Chart */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CalendarRange size={18} className="text-[#F7BA45]" />
                <span>Günlük İstatistikler</span>
              </h2>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-slate-600">Görüntülenme</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-[#F7BA45] rounded-full"></div>
                  <span className="text-slate-600">Tıklama</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-slate-600">Dönüşüm</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-80">
              {/* Chart Container */}
              <div className="relative h-full">
                {/* Y-axis labels */}
                <div className="absolute left-0 top-0 bottom-0 w-12 flex flex-col justify-between text-xs text-slate-500">
                  <div>1000</div>
                  <div>750</div>
                  <div>500</div>
                  <div>250</div>
                  <div>0</div>
                </div>
                
                {/* Chart Area */}
                <div className="ml-12 h-full flex items-end">
                  <div className="flex-1 h-full flex items-end">
                    {/* Horizontal grid lines */}
                    <div className="absolute inset-0 ml-12 flex flex-col justify-between pointer-events-none">
                      <div className="border-b border-slate-200 h-1/4"></div>
                      <div className="border-b border-slate-200 h-1/4"></div>
                      <div className="border-b border-slate-200 h-1/4"></div>
                      <div className="border-b border-slate-200 h-1/4"></div>
                    </div>
                    
                    {/* Bars */}
                    <div className="flex-1 flex items-end justify-between h-full relative">
                      {dailyStats.map((day, index) => (
                        <div key={day.date} className="flex-1 flex items-end justify-center group">
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 bg-slate-800 text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                            <div className="font-medium">{new Date(day.date).toLocaleDateString('tr-TR')}</div>
                            <div>Görüntülenme: {day.views}</div>
                            <div>Tıklama: {day.clicks}</div>
                            <div>Dönüşüm: {day.conversions}</div>
                          </div>
                          
                          <div className="w-full px-0.5 flex items-end justify-center">
                            {/* Views Bar */}
                            <div 
                              className="w-2 bg-blue-500 rounded-t transition-all duration-500 mx-0.5"
                              style={{ height: `${(day.views / 1000) * 100}%` }}
                            ></div>
                            
                            {/* Clicks Bar */}
                            <div 
                              className="w-2 bg-[#F7BA45] rounded-t transition-all duration-500 mx-0.5"
                              style={{ height: `${(day.clicks / 1000) * 100}%` }}
                            ></div>
                            
                            {/* Conversions Bar */}
                            <div 
                              className="w-2 bg-green-500 rounded-t transition-all duration-500 mx-0.5"
                              style={{ height: `${(day.conversions / 1000) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* X-axis labels */}
                <div className="ml-12 mt-2 flex justify-between text-xs text-slate-500">
                  {dailyStats.filter((_, i) => i % 5 === 0).map(day => (
                    <div key={day.date} className="w-10 text-center">
                      {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;